import { useCallback, useEffect, useState } from 'react'

const STORAGE_PREFIX = 'todone.project.outline.done.'

/**
 * Local-only "I've done this step" tracker for the Project Viewer's basic outline. Lets a
 * user check off actions while reading without starting the project (no Following row, no
 * server commitment).
 *
 * <p>Persists per-project in localStorage. Anonymous and signed-in viewers each get their own
 * browser-local list — explicitly NOT synced to the server. Starting the project (the proper
 * Doer flow) opens a separate {@code DoingView} with real `step_progress` rows.
 *
 * <p>TODO: when the user starts a project, optionally migrate their local checks into the
 * server-side progress so they don't lose progress when committing to a Following.
 */
export interface StepDone {
  isDone: (stepId: string) => boolean
  toggle: (stepId: string) => void
}

export const useStepDone = (projectId: string): StepDone => {
  const [done, setDone] = useState<Set<string>>(() => readStored(projectId))

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(STORAGE_PREFIX + projectId, JSON.stringify([...done]))
  }, [done, projectId])

  const isDone = useCallback((stepId: string) => done.has(stepId), [done])
  const toggle = useCallback((stepId: string) => {
    setDone((prev) => {
      const next = new Set(prev)
      if (next.has(stepId)) next.delete(stepId)
      else next.add(stepId)
      return next
    })
  }, [])

  return { isDone, toggle }
}

const readStored = (projectId: string): Set<string> => {
  if (typeof window === 'undefined') return new Set()
  const raw = window.localStorage.getItem(STORAGE_PREFIX + projectId)
  if (!raw) return new Set()
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed))
      return new Set(parsed.filter((x): x is string => typeof x === 'string'))
  } catch {
    /* fall through */
  }
  return new Set()
}
